import { Gavel, Package, type LucideIcon } from 'lucide-react';

/**
 * One row of either flat procurement vocabulary.
 *
 * **Not a `PpmpLineItem`**, which is one thing a plan intends to buy. This is the vocabulary
 * that line is described with, maintained under Procurement → Procurement Planning → Settings
 * with its own `procurement-catalogs.*` keys.
 *
 * **Every nullable field is optional, not `| null`.** The API sets
 * `JsonIgnoreCondition.WhenWritingNull`, so a null property is not sent at all — it arrives as
 * `undefined`. Check falsiness, never identity.
 */
export interface ProcurementCatalogEntry {
  id: string;
  name: string;
  description?: string | null;

  /**
   * Live PPMP lines naming it. Derived on every read, never stored.
   *
   * What the delete confirmation counts, so a reader is told what retiring it leaves behind:
   * every line already filed under it keeps the snapshotted word and reads exactly as it did.
   */
  lineItemCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the modal sends. */
export interface ProcurementCatalogRequest {
  name: string;
  description?: string | null;
}

/** One row as a PPMP line's picker needs it. */
export interface ProcurementCatalogOption {
  id: string;
  name: string;
  description?: string | null;
}

/** The route slug of one vocabulary — the segment the API and the SPA both address it by. */
export type ProcurementCatalogSlug = 'types' | 'modes';

/**
 * Everything one vocabulary's pages differ by.
 *
 * **Two vocabularies, one set of screens** — they are one shape, a name and a description over
 * a count of the lines filed under them, so two copies of an index page and two of a profile
 * page would be four files whose sixth divergence nobody notices. This is the L&D vocabularies'
 * own descriptor, one suite along, and the reason they share ONE permission module is the same:
 * maintaining the two is one administrative job.
 */
export interface ProcurementCatalogDescriptor {
  slug: ProcurementCatalogSlug;

  /** The heading, the breadcrumb leaf and the Settings row. */
  label: string;

  /** What one row IS, for a button, a dialog title and a message. */
  noun: string;

  /** The plural of `noun`, for an empty state and a count. */
  nounPlural: string;

  /** One line on the Settings menu row, saying what it configures. */
  description: string;

  /**
   * What the list says when it holds nothing yet.
   *
   * **Per vocabulary rather than composed from `nounPlural`.** One sentence covering both is
   * one bent to fit, and it read *"Add the ones this organization procures under"* over a list
   * of procurement TYPES — a clause with nothing to attach to, because a type is not something
   * anybody procures under. An empty state is the only prose a reader meets on a list they
   * cannot act on yet, so it names examples rather than restating the heading.
   */
  emptyMessage: string;

  /** The word above the name column, which is the row's anchor. */
  columnHeader: string;

  /** What the form asks under the name field. */
  fieldDescription: string;

  /** The record mark on the profile header, and the Settings menu row's icon. */
  icon: LucideIcon;

  /** Where a form that picks from it says a new row is maintained. */
  settingsPath: string;
}

/**
 * The two, in the order a procurement line asks them: WHAT is being bought, then HOW.
 */
export const PROCUREMENT_CATALOGS: readonly ProcurementCatalogDescriptor[] = [
  {
    slug: 'types',
    label: 'Procurement Types',
    noun: 'procurement type',
    nounPlural: 'procurement types',
    description:
      'What kind of thing a procurement line buys — goods, infrastructure, consulting services.',
    emptyMessage:
      'No procurement types yet. Add the kinds of thing this organization buys — goods, infrastructure, consulting services.',
    columnHeader: 'Procurement type',
    fieldDescription: 'What kind of thing is being procured, as the procurement plan reports it.',
    icon: Package,
    settingsPath: '/procurement/procurement-planning/settings/procurement-types',
  },
  {
    slug: 'modes',
    label: 'Modes of Procurement',
    noun: 'mode of procurement',
    nounPlural: 'modes of procurement',
    /*
      The description names the statute, because that is what makes the vocabulary worth
      maintaining rather than typing: RA 9184 caps each alternative mode, so the figure summed
      by mode is one an auditor checks against a limit.
    */
    description:
      'How a procurement line is carried out — competitive bidding, and the alternative modes RA 9184 allows.',
    emptyMessage:
      'No modes of procurement yet. Add the modes this organization procures under — competitive bidding, and the alternatives RA 9184 allows.',
    columnHeader: 'Mode',
    fieldDescription: 'How the procurement is carried out, in the statute’s own words.',
    icon: Gavel,
    settingsPath: '/procurement/procurement-planning/settings/procurement-modes',
  },
];

/** The descriptor for one slug, or undefined for a slug this product does not have. */
export function procurementCatalog(slug: string): ProcurementCatalogDescriptor | undefined {
  return PROCUREMENT_CATALOGS.find((c) => c.slug === slug);
}

/**
 * One entry in the catalog a PPMP line's description is drawn from.
 *
 * **`PpmpItemCatalogEntry`, never `PpmpItem`** — the second would sit one letter from
 * `PpmpLineItem`, which is a line under a plan rather than a row in this catalog. That is the
 * `Position` / `PlantillaPosition` trap, and naming it out of existence costs nothing.
 */
export interface PpmpItemCatalogEntry {
  id: string;

  /** The specification, as sanitized HTML. Rendered through `<RichTextDocument>`. */
  itemDescription: string;

  /**
   * The plain text of it.
   *
   * What the anchor column shows and what the search matches — `LIKE` over stored markup
   * matches every specification CONTAINING a table rather than the ones that mention one.
   */
  itemDescriptionText: string;

  unit: string;

  /** Null is UNPRICED — nobody has looked — which is not the same fact as a cost of zero. */
  estimatedUnitCost?: number | null;

  /** Live PPMP lines drawn from this entry. Derived on every read. */
  lineItemCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

/** What the catalog's own modal sends. The plain projection is derived server-side. */
export interface PpmpItemCatalogRequest {
  itemDescription: string;
  unit: string;
  estimatedUnitCost?: number | null;
}

/**
 * One entry as the PPMP line form's description picker needs it.
 *
 * It carries the WHOLE entry rather than a label and an id, because choosing one snapshots the
 * description, the unit and the cost onto the line.
 */
export interface PpmpItemCatalogOption {
  id: string;
  itemDescription: string;
  itemDescriptionText: string;
  unit: string;
  estimatedUnitCost?: number | null;
}
